import { useState } from 'react';
import {
    FallingPetalsCSS,
    FallingPetalsCanvas,
    FallingPetalsThree,
    FallingPetalsImage,
    FallingLotusModel3D,
    FallingLotusPhoto,
    FallingWindPhoto,
} from '@shared/components/falling-petals/FallingPetals';
import './FallingPetalsDemoPage.css';

// 原圖沒有真正透明背景（棋盤格是烤進圖片裡的），已用程式去背成真透明 PNG
const LOTUS_PHOTO_SRC = '/image/fall/lotus-photo-cutout.png';
// 已是正確去背的透明 PNG，可直接使用
const BODHI_PHOTO_SRC = '/image/fall/pu-ti-leaf.png';

const ENGINES = [
    { id: 'wind-lotus', label: '風力模擬／蓮花（最新）', Component: FallingWindPhoto, desc: '即時風力模擬（非固定路徑）：requestAnimationFrame 逐幀計算，全域風力正弦波會不定期換氣（陣風），每片有獨立隨機速度與旋轉，參考 Sarah Higley Falling Leaves 演算法，效果比固定拋物線路徑更隨機自然', supportsType: false, supportsColor: false },
    { id: 'wind-bodhi', label: '風力模擬／菩提葉（最新）', Component: FallingWindPhoto, desc: '同樣的即時風力模擬，多片菩提葉同時翻滾飄落，三軸獨立旋轉速度', supportsType: false, supportsColor: false },
    { id: 'photo', label: '寫實照片素材／蓮花（固定路徑）', Component: FallingLotusPhoto, desc: '寫實 3D 渲染蓮花照片，已去背處理成真透明背景（測試用，尚未取得正式授權，正式上線前需更換）。單張、固定從畫面正中央飄落，左右搖擺幅度大、統一偏慢速度，蓮花圖片本身不旋轉', supportsType: false, supportsColor: false },
    { id: 'photo-bodhi', label: '寫實照片素材／菩提葉（固定路徑）', Component: FallingLotusPhoto, desc: '菩提葉照片，同樣位置與飄落路徑，圖片本體持續旋轉翻滾，符合真實落葉飄落的樣子', supportsType: false, supportsColor: false },
    { id: 'model3d', label: '真 3D 立體模型', Component: FallingLotusModel3D, desc: '每片花瓣是真正的立體幾何體（Three.js ExtrudeGeometry 擠出＋彎曲），不是平面貼圖，任何角度旋轉都正確，不會轉到側面就變一條線消失。僅支援蓮花', supportsType: false, supportsColor: true },
    { id: 'img-silhouette', label: 'A 素材／側視剪影', Component: FallingPetalsImage, variant: 'silhouette', desc: '成熟美術素材（CC0，取自 freesvg.org），側視剪影單色，輪廓俐落分明、可自訂顏色', supportsType: false, supportsColor: true },
    { id: 'img-gradient', label: 'B 素材／側視漸層彩色', Component: FallingPetalsImage, variant: 'gradient', desc: '成熟美術素材，側視漸層彩色（已去除莖梗），保留花心深、瓣尖淺的立體層次，可依主色重新調色', supportsType: false, supportsColor: true },
    { id: 'img-mandala', label: 'C 素材／俯視圖騰', Component: FallingPetalsImage, variant: 'mandala', desc: '成熟美術素材，俯視放射對稱線條圖騰，對稱工整、可自訂顏色', supportsType: false, supportsColor: true },
    { id: 'css', label: 'CSS 程式繪製', Component: FallingPetalsCSS, desc: '程式碼手繪花朵，效能最好。蓮花固定視角只搖擺；菩提葉維持翻轉飄落', supportsType: true, supportsColor: true },
    { id: 'canvas', label: 'Canvas 2D 程式繪製', Component: FallingPetalsCanvas, desc: '程式碼手繪花朵，requestAnimationFrame 粒子系統，風向擺動更自然', supportsType: true, supportsColor: true },
    { id: 'three', label: 'Three.js 十字貼圖', Component: FallingPetalsThree, desc: '平面貼圖十字交叉（cross-billboard），蓮花只水平旋轉、菩提葉三軸翻轉', supportsType: true, supportsColor: true },
];

const COLOR_PRESETS = [
    { label: '金黃', value: '#F5C97B' },
    { label: '粉白', value: '#FBE4EC' },
    { label: '嫩綠', value: '#8FBC8F' },
    { label: '淡紫', value: '#C9B8E8' },
];

export default function FallingPetalsDemoPage() {
    const [engineId, setEngineId] = useState('wind-lotus');
    const [type, setType] = useState('lotus');
    const [count, setCount] = useState(1);
    const [color, setColor] = useState('#F5C97B');

    const current = ENGINES.find((e) => e.id === engineId);
    const Component = current.Component;
    const extraProps = {
        ...(current.supportsType ? { type } : {}),
        ...(current.variant ? { variant: current.variant } : {}),
        ...(current.id === 'photo' ? { imageSrc: LOTUS_PHOTO_SRC, size: 110 } : {}),
        ...(current.id === 'photo-bodhi' ? { imageSrc: BODHI_PHOTO_SRC, spin: true } : {}),
        ...(current.id === 'wind-lotus' ? { imageSrc: LOTUS_PHOTO_SRC, size: 90 } : {}),
        ...(current.id === 'wind-bodhi' ? { imageSrc: BODHI_PHOTO_SRC, size: 60, spin: true } : {}),
    };

    return (
        <div className="petals-demo-page">
            <div className="petals-demo-stage">
                <Component key={`${engineId}-${type}-${count}-${color}`} count={count} color={color} {...extraProps} />
                <div className="petals-demo-stage-label">
                    {current.label}
                </div>
            </div>

            <div className="petals-demo-panel">
                <h1>飄落特效測試頁</h1>
                <p className="petals-demo-hint">此頁僅供測試比較，不會加入正式導覽</p>

                <div className="petals-demo-group">
                    <label>渲染引擎 / 素材</label>
                    <div className="petals-demo-btn-row">
                        {ENGINES.map((e) => (
                            <button
                                key={e.id}
                                className={e.id === engineId ? 'active' : ''}
                                onClick={() => setEngineId(e.id)}
                            >
                                {e.label}
                            </button>
                        ))}
                    </div>
                    <p className="petals-demo-desc">{current.desc}</p>
                </div>

                {current.supportsType && (
                    <div className="petals-demo-group">
                        <label>形狀</label>
                        <div className="petals-demo-btn-row">
                            <button className={type === 'lotus' ? 'active' : ''} onClick={() => setType('lotus')}>蓮花</button>
                            <button className={type === 'bodhi' ? 'active' : ''} onClick={() => setType('bodhi')}>菩提葉</button>
                        </div>
                    </div>
                )}

                {current.supportsColor && (
                    <div className="petals-demo-group">
                        <label>顏色</label>
                        <div className="petals-demo-btn-row">
                            {COLOR_PRESETS.map((c) => (
                                <button
                                    key={c.value}
                                    className={color === c.value ? 'active' : ''}
                                    onClick={() => setColor(c.value)}
                                    style={{ '--swatch': c.value }}
                                >
                                    <span className="petals-demo-swatch" style={{ background: c.value }} />
                                    {c.label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                <div className="petals-demo-group">
                    <label>數量：{count}</label>
                    <input
                        type="range"
                        min="1"
                        max="80"
                        value={count}
                        onChange={(e) => setCount(Number(e.target.value))}
                    />
                </div>
            </div>
        </div>
    );
}
